import { CategoriesResponse } from '@shopby/shop-sdk'
import { AxiosResponse } from 'axios'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import 'swiper/css'
import 'swiper/css/pagination'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FestivalType, ShopbyFestivalComponents } from '../../@types/aboutfishing'
import { ProfileRootType } from '../../@types/auth'
import { getProfile } from '../../api/auth'
import { getFestivals } from '../../api/login'
import { getCategories } from '../../api/product'
import FestivalAnchorNavigation from '../../components/festival/FestivalAnchorNavigation'
import FestivalCouponModule from '../../components/festival/FestivalCouponModule'
import FestivalEvent from '../../components/festival/FestivalEvent'
import FestivalGrid from '../../components/festival/FestivalGrid'
import FestivalImage from '../../components/festival/FestivalImage'
import { useCommonContext } from '../../components/provider/CommonProvider'
import '../../css/Event.css'
import '../../css/home.css'
import { AFWebLinkType, handlePostMessage, RNPostMessage } from '../../js/postMessage'
import FestivalHorizontal from './FestivalHorizontal'
import FestivalManyCollection from './FestivalManyCollection'
import HotDeal from './HotDeal'
import { useAppContext } from '../../components/provider/AppProvider'

export default function Festival() {
  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()
  const navigate = useNavigate()
  const params = useParams()

  const [festivalData, setFestivalData] = useState<FestivalType | null>(null)
  const [userData, setUserData] = useState<ProfileRootType | null>()
  const [category, setCategory] = useState<CategoriesResponse>({}) //전상품 카테고리
  const [memberCheckPoint, setMemberCheckPoint] = useState<boolean>(false) // 적립금 받을 수 있는 회원인지 체크
  const [fixedPosition, setFixedPosition] = useState<string>('')
  const sectionRefs = useRef(new Map())

  const checkApiError = (code: 'E0703' | 'E0701') => {
    const errorMessages: { [key in 'E0703' | 'E0701']: string } = {
      E0703: '종료된 기획전/이벤트 입니다.',
      E0701: '존재하지 않는 기획전/이벤트 입니다.',
    }

    const errorMessage = errorMessages[code]
    if (!errorMessage) return false

    alert(errorMessage)
    if (isAppView) {
      RNPostMessage(AFWebLinkType.goBack)
    } else {
      navigate('/')
    }
    return true
  }

  // 기획전 API 호출 성공 시 실행
  const handleSuccess = (res: AxiosResponse) => {
    if (!res.data.festival && checkApiError(res.data.code)) return
    setFestivalData(res.data)
  }

  // 기획전 API 호출 실패 시 실행
  const handleError = (err: Error) => {
    alert('잠시후에 확인해주세요.')
    if (isAppView) {
      RNPostMessage(AFWebLinkType.goBack)
    } else {
      navigate('/')
    }
    console.error('Error fetching festival data:', err)
  }

  // 기획전 API 호출 로직
  const fetchFestivalData = async (url: string) => {
    try {
      const res = await getFestivals(url) // 기획전 API 호출
      handleSuccess(res)
      return // 성공하면 함수 종료
    } catch (err) {
      handleError(err as Error)
    }
  }

  useEffect(() => {
    if (params.url) fetchFestivalData(params.url)
  }, [navigate])

  //상품진열 관리 가져오기
  const fetchCategories = useCallback(async () => {
    try {
      const res = await getCategories()
      setCategory(res.data)
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const fetchUserProfile = useCallback(async () => {
    if (hasAccessToken) {
      try {
        const res = await getProfile()
        setUserData(res.data)
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
      }
    }
  }, [hasAccessToken])

  useEffect(() => {
    fetchUserProfile()
  }, [hasAccessToken])

  const compareDate = (joinDate: string) => {
    // 기준일 (2024-08-29)
    if (joinDate) {
      const targetDate = new Date('2024-08-30')
      // 가입 날짜
      const memberJoinDate = new Date(joinDate)

      // 회원가입 날짜가 타겟날짜보다 이후이면 true
      setMemberCheckPoint(memberJoinDate >= targetDate)
    }
  }

  // 날짜 비교하기
  useEffect(() => {
    if (userData) {
      const date = userData?.joinYmdt?.split(' ', 1).join('')
      compareDate(date)
    }
  }, [userData])

  // 상단 header, anchorEl 높이값
  const headerEl = document.querySelector('.header_wrap')
  const anchorEl = document.querySelector('.anchor_tab')
  useEffect(() => {
    if (headerEl && anchorEl) {
      const fixedValue = isMobile
        ? headerEl.clientHeight + anchorEl.clientHeight - 2 + 'px'
        : 84 + anchorEl.clientHeight - 2 + 'px'
      setFixedPosition(fixedValue)
    }
  }, [headerEl, anchorEl])
  const getScrollMarginTop = fixedPosition

  const renderAnchorNavigation = (el: ShopbyFestivalComponents) => {
    if (!sectionRefs.current.has(el.id)) {
      sectionRefs.current.set(el.id, React.createRef())
    }
    return <FestivalAnchorNavigation key={el.id} el={el} festivalData={festivalData} sectionRefs={sectionRefs} />
  }

  const renderCoupon = (el: ShopbyFestivalComponents, ref: React.RefObject<HTMLDivElement>) => (
    <FestivalCouponModule
      key={el.id}
      ref={ref}
      el={el}
      userData={userData}
      memberCheckPoint={memberCheckPoint}
      festivalData={festivalData}
      getScrollMarginTop={fixedPosition}
    />
  )

  const renderImage = (el: ShopbyFestivalComponents, ref: React.RefObject<HTMLDivElement>) => (
    <FestivalImage
      key={el.id}
      el={el}
      innerRef={ref}
      isMobile={isMobile}
      getScrollMarginTop={fixedPosition}
      handlePostMessage={handlePostMessage}
    />
  )

  const renderGrid = (el: ShopbyFestivalComponents, ref: React.RefObject<HTMLDivElement>) => (
    <FestivalGrid key={el.id} ref={ref} el={el} getScrollMarginTop={fixedPosition} />
  )

  const renderHorizontalScroll = (el: ShopbyFestivalComponents, ref: React.RefObject<HTMLDivElement>) => (
    <div
      className='horizontal-scroll section center'
      id={`section${el.id}`}
      style={{ scrollMarginTop: getScrollMarginTop as string }}
      ref={ref}
      key={el.id}
    >
      <FestivalHorizontal el={el} isMobile={isMobile} />
    </div>
  )

  const renderProductHighlight = (el: ShopbyFestivalComponents, ref: React.RefObject<HTMLDivElement>) => (
    <div className='home_wrap section center' id={`section${el.id}`} ref={ref} key={el.id}>
      <div className='hot_deal festival'>
        <div className='head_img'>
          <img src={isMobile ? el.mobileImageUrl : el.pcImageUrl} alt='' />
        </div>
        <HotDeal el={el} category={category} />
      </div>
    </div>
  )

  const renderManyCollection = (el: ShopbyFestivalComponents, ref: React.RefObject<HTMLDivElement>) => (
    <div key={el.id} className='many-collection section center' id={`section${el.id}`} ref={ref}>
      <div className='head_img'>
        <img src={isMobile ? el.mobileImageUrl : el.pcImageUrl} alt='' />
      </div>
      <FestivalManyCollection el={el} festivalData={festivalData} />
    </div>
  )

  const renderEventProduct = (el: ShopbyFestivalComponents) => (
    <FestivalEvent eventNo={el.shopbyFestivalComponentCollections[0]?.no} key={el.id} />
  )

  const createSectionRef = (id: number | string) => {
    if (!sectionRefs.current.has(id)) sectionRefs.current.set(id, React.createRef())
    return sectionRefs.current.get(id)
  }

  const renderFestivalComponents = (
    components: ShopbyFestivalComponents[],
    createSectionRef: (id: number | string) => React.RefObject<HTMLDivElement>
  ) => {
    return components.map((el: ShopbyFestivalComponents, index: number) => {
      if (index === 0) return null

      const ref = createSectionRef(el.id)

      switch (el.type) {
        case 'anchor':
          return renderAnchorNavigation(el)
        case 'benefit':
          return renderCoupon(el, ref)
        case 'image':
          return renderImage(el, ref)
        case 'grid':
          return renderGrid(el, ref)
        case 'horizontal-scroll':
          return renderHorizontalScroll(el, ref)
        case 'product-highlight':
          return renderProductHighlight(el, ref)
        case 'many-collection':
          return renderManyCollection(el, ref)
        case 'event-product':
          return renderEventProduct(el)
        default:
          return null
      }
    })
  }

  return (
    <>
      <div className='main_wrap'>
        <div className='event_top'>
          {festivalData !== null && festivalData.festival.shopbyFestivalComponents[0].isMain && (
            <img
              src={
                isMobile
                  ? festivalData.festival.shopbyFestivalComponents[0].mobileImageUrl
                  : festivalData.festival.shopbyFestivalComponents[0].pcImageUrl
              }
              alt=''
            />
          )}
        </div>
      </div>
      <div className='event_wrap'>
        {festivalData !== null &&
          renderFestivalComponents(festivalData.festival.shopbyFestivalComponents, createSectionRef)}
      </div>
    </>
  )
}

// Tab Swiper 2Depth 영역
export const SwiperWraps = styled(Swiper)``

export const SwiperSlideItems = styled(SwiperSlide)`
  width: auto !important;
  & .on {
    & a {
      color: var(--colWhite);
      background: var(--colBlack);
      border: 1px transparent solid;
    }
  }
  & a {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--colGray76);
    background: var(--colWhite);
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    word-break: keep-all;
    height: 32px;
    border-radius: 32px;
    border: 1px var(--colGray3) solid;
    padding: 8px 14px;
    white-space: nowrap;
  }
  @media screen and (min-width: 768px) {
    & .on {
      & a {
        color: var(--colWhite);
        background: var(--colBlack);
        border: 1px transparent solid;
      }
    }
    & a {
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--colGray6);
      background: var(--colWhite);
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      word-break: keep-all;
      height: 40px;
      border-radius: 32px;
      border: 1px var(--colGray3) solid;
      padding: 10px 16px;
      white-space: nowrap;
    }
  }
`
